import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import {
  Package, Search, ExternalLink, ChevronLeft, ChevronRight,
  Loader2, Filter, X, Check, XCircle
} from 'lucide-react'
import { useDebounce } from '@/hooks/useDebounce'

interface Product {
  id_product: number
  reference: string
  name: string
  price: number
  wholesale_price: number
  active: boolean
  date_add: string | null
  date_upd: string | null
  stock: number
  category_id: number | null
  category_name: string
  link_rewrite: string
  category_link_rewrite: string
}

interface ProductListResponse {
  products: Product[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// URL base de la tienda PrestaShop
const SHOP_BASE_URL = 'https://www.jvs-informatica.com'

export default function ProductsListPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Filtros
  const [filterName, setFilterName] = useState('')
  const [filterReference, setFilterReference] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | null>(null)

  // Debounce de filtros para evitar demasiadas peticiones
  const debouncedName = useDebounce(filterName, 300)
  const debouncedReference = useDebounce(filterReference, 300)
  const debouncedCategory = useDebounce(filterCategory, 300)

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [debouncedName, debouncedReference, debouncedCategory, filterActive])

  // Query para obtener productos
  const productsQuery = useQuery({
    queryKey: ['prestashop-products', page, pageSize, debouncedName, debouncedReference, debouncedCategory, filterActive],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('page_size', String(pageSize))
      if (debouncedName) params.append('search_name', debouncedName)
      if (debouncedReference) params.append('search_reference', debouncedReference)
      if (debouncedCategory) params.append('search_category', debouncedCategory)
      if (filterActive !== null) params.append('active_only', String(filterActive))

      const res = await axios.get(`/api/products/prestashop/list?${params.toString()}`)
      return res.data as ProductListResponse
    }
  })

  const products = productsQuery.data?.products || []
  const total = productsQuery.data?.total || 0
  const totalPages = productsQuery.data?.total_pages || 1

  // Generar URL del producto en la tienda
  // Formato: /{category_link_rewrite}/{id_product}-{product_link_rewrite}.html
  const getProductUrl = (product: Product) => {
    if (!product.link_rewrite || !product.category_link_rewrite) return null
    return `${SHOP_BASE_URL}/${product.category_link_rewrite}/${product.id_product}-${product.link_rewrite}.html`
  }

  // Limpiar todos los filtros
  const clearFilters = () => {
    setFilterName('')
    setFilterReference('')
    setFilterCategory('')
    setFilterActive(null)
  }

  const hasActiveFilters = filterName || filterReference || filterCategory || filterActive !== null

  // Formatear precio
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
          <Package className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </span>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Listado de Productos</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {total.toLocaleString('es-ES')} productos encontrados
          </p>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-500" />
            <h2 className="font-semibold text-slate-900 dark:text-white">Filtros</h2>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-1 text-sm text-slate-600 dark:text-slate-400 hover:text-danger-600 dark:hover:text-red-400 transition-colors"
            >
              <X className="w-4 h-4" />
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Filtro por nombre */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Nombre
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Buscar por nombre..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filtro por referencia */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Referencia
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={filterReference}
                onChange={(e) => setFilterReference(e.target.value)}
                placeholder="Buscar por referencia..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filtro por categoría */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Categoría
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                placeholder="Buscar por categoría..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filtro por estado */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Estado
            </label>
            <select
              value={filterActive === null ? '' : filterActive ? 'true' : 'false'}
              onChange={(e) => {
                if (e.target.value === '') setFilterActive(null)
                else setFilterActive(e.target.value === 'true')
              }}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-blue-500"
            >
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        {productsQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            <span className="ml-3 text-slate-600 dark:text-slate-400">Cargando productos...</span>
          </div>
        ) : productsQuery.isError ? (
          <div className="flex items-center justify-center py-20 text-danger-500">
            <XCircle className="w-6 h-6 mr-2" />
            Error al cargar productos
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500 dark:text-slate-400">
            <Package className="w-12 h-12 mb-3 opacity-50" />
            <p>No se encontraron productos</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-primary-500 hover:underline"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Referencia
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Categoría
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Precio Coste
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Precio Venta
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Tienda
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {products.map((product) => {
                    const productUrl = getProductUrl(product)
                    return (
                      <tr
                        key={product.id_product}
                        className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-slate-900 dark:text-white">
                            {product.reference || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs">
                            <p className="text-sm text-slate-900 dark:text-white truncate" title={product.name}>
                              {product.name || 'Sin nombre'}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              ID: {product.id_product}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700 dark:text-slate-300 truncate block max-w-[200px]" title={product.category_name}>
                            {product.category_name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {formatPrice(product.wholesale_price)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {formatPrice(product.price)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            product.stock > 0
                              ? 'bg-success-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-danger-100 text-red-800 dark:bg-danger-900/30 dark:text-danger-400'
                          }`}>
                            {product.stock}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {product.active ? (
                            <span className="inline-flex items-center gap-1 text-success-600 dark:text-success-400">
                              <Check className="w-4 h-4" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500">
                              <XCircle className="w-4 h-4" />
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {productUrl ? (
                            <a
                              href={productUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary-600 dark:text-primary-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                              title="Abrir en la tienda"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Ver
                            </a>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} de {total.toLocaleString('es-ES')}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setPage(1)
                  }}
                  className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>

                <span className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400">
                  Página {page} de {totalPages}
                </span>

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
